import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import { captureEvent } from "~/helpers/capture_posthog_event";
import {
  getLocalStorageValue,
  setLocalStorageValue,
} from "~/helpers/localStorageHelpers";
import { COURTED_FEATURE_FLAG } from "~/pages/Data/components/MlsImport/Form/constants/providerConstants";
import CourtedSections from "./CourtedSections";
import {
  COURTED_ATTRIBUTES_SHAPE,
  computeSectionPresence,
  PANEL_ID,
  STORAGE_KEY,
} from "./courtedConstants";

const LeadDrawerCourtedDetails = ({ lead, module }) => {
  const courtedVariant = useFeatureFlagVariantKey(COURTED_FEATURE_FLAG);
  const [isOpen, setIsOpen] = useState(() =>
    getLocalStorageValue(STORAGE_KEY, false),
  );

  const handleToggle = useCallback(
    (e) => {
      e.preventDefault();
      const next = !isOpen;
      setIsOpen(next);
      setLocalStorageValue(STORAGE_KEY, next);
      if (next) {
        captureEvent("courted_panel_expanded", { module });
      }
    },
    [isOpen, module],
  );

  const attrs = lead?.courted_attributes;

  const sections = useMemo(() => computeSectionPresence(attrs), [attrs]);
  const hasAnySection = useMemo(
    () => Object.values(sections).some(Boolean),
    [sections],
  );

  // Treat the loading state (undefined while PostHog resolves the flag) as
  // hidden so users in the "not_displayed" variant don't see a brief flash.
  if (courtedVariant !== "displayed" || !attrs || !hasAnySection) {
    return null;
  }

  return (
    <div className="bt pt15">
      <div className="d-flex align-items-center justify-content-between mb15">
        <h4 className="mb-0">Courted</h4>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleToggle}
          aria-label={
            isOpen ? "Collapse Courted panel" : "Expand Courted panel"
          }
          aria-expanded={isOpen}
          aria-controls={PANEL_ID}
        >
          {isOpen ? (
            <FontAwesomeIcon icon={["far", "fa-minus"]} />
          ) : (
            <FontAwesomeIcon icon={["far", "fa-plus"]} />
          )}
        </button>
      </div>

      <Collapse in={isOpen} mountOnEnter unmountOnExit>
        <div id={PANEL_ID}>
          <table className="table table-flush table-sm no-border">
            <tbody>
              <CourtedSections attrs={attrs} sections={sections} />
            </tbody>
          </table>
        </div>
      </Collapse>
    </div>
  );
};

LeadDrawerCourtedDetails.propTypes = {
  lead: PropTypes.shape({
    courted_attributes: COURTED_ATTRIBUTES_SHAPE,
  }),
  module: PropTypes.oneOf(["retention", "recruiting"]).isRequired,
};

export default LeadDrawerCourtedDetails;
